import { h, Component } from 'preact'
import { differenceInSeconds, addSeconds } from 'date-fns'
import { nowDate } from '../helpers/dateHelpers'

const SECONDS_IN_A_WEEK = 3600 * 24 * 7

class Countdown extends Component {
    constructor(props) {
        super(props)
        this.state = { current: 'DATE_TIME', remainingTime: null, firstUpdateTimeout: true }
        this.showSpinnerTimeout = null
        this.showCountdownTimeout = null
        this.updateCountdownInterval = null
        this.firstUpdateCountdownTimeout = null
        this.countdownOverTimeout = null
        
        this.setup = this.setup.bind(this)
        this.setTimeoutToShowSpinner = this.setTimeoutToShowSpinner.bind(this)
        this.setTimeoutToShowCountdown = this.setTimeoutToShowCountdown.bind(this)
        this.setIntervalToUpdateCountdown = this.setIntervalToUpdateCountdown.bind(this)
        this.setTimeoutToUpdateCountdown = this.setTimeoutToUpdateCountdown.bind(this)
        this.setCountdownOverTimeout = this.setCountdownOverTimeout.bind(this)
        this.clearTimers = this.clearTimers.bind(this)
    }

    componentWillMount() {
        this.setup(this.props)
    }

    componentWillUnmount() {
        this.clearTimers()
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.timezoneOffset !== this.props.timezoneOffset
            || nextProps.date !== this.props.date) {
            this.clearTimers()
            this.setup(nextProps)
        }

        if (nextProps.shouldFinish && nextProps.shouldFinish !== this.props.shouldFinish) {
            this.clearTimers()
            this.setState({ current: 'OVER' })
            nextProps.onCountdownFinished && nextProps.onCountdownFinished()
        }
    }

    clearTimers() {
        clearTimeout(this.showSpinnerTimeout)
        clearTimeout(this.showCountdownTimeout)
        clearInterval(this.updateCountdownInterval)
        clearTimeout(this.firstUpdateCountdownTimeout)
        clearTimeout(this.countdownOverTimeout)
    }

    setup(props) {
        const secondsBeforeIsOver = differenceInSeconds(props.date, nowDate(props.timezoneOffset))
        if (!props.shouldFinish && secondsBeforeIsOver > (-props.spinnerDurationSeconds / 2 || 0)) {
            if (this.shouldShowSpinner(props)) {
                this.setState({ current: 'SPINNER' })
            } else if (this.shouldShowCountdown(props)) {
                this.showCountdown(props)
            } else {
                this.setState({ current: 'DATE_TIME' })
                this.setTimeoutToShowCountdown(props)
            }
            this.setCountdownOverTimeout(props)
        } else {
            this.setState({ current: 'OVER' })
        }
    }

    showCountdown(props) {
        this.setState({
            current: 'COUNTDOWN',
            remainingTime: this.getRemainingTime(props)
        })
        this.setTimeoutToShowSpinner(props)
        this.setTimeoutToUpdateCountdown(props)
    }

    getRemainingTime({ date, timezoneOffset, updateIntervalSeconds }) {
        let numSeconds = differenceInSeconds(addSeconds(date, updateIntervalSeconds), nowDate(timezoneOffset))
        const result = { days: Math.floor(numSeconds / (24 * 3600)) }
        
        numSeconds %= 24 * 3600
        result.hours = Math.floor(numSeconds / 3600)
        
        numSeconds %= 3600
        result.minutes = Math.floor(numSeconds / 60)
        
        numSeconds %= 60
        result.seconds = numSeconds

        return result
    }

    setIntervalToUpdateCountdown(props) {
        this.setState({ remainingTime: this.getRemainingTime(props) })
        if (props.updateIntervalSeconds) {
            this.updateCountdownInterval = setInterval(
                () => {
                    this.setState({ remainingTime: this.getRemainingTime(props) })
                }, props.updateIntervalSeconds * 1000)
        }
    }

    setTimeoutToUpdateCountdown(props) {
        const { updateIntervalSeconds, date, timezoneOffset } = props
        const secondsBeforeIsOver = differenceInSeconds(date, nowDate(timezoneOffset))
        this.firstUpdateCountdownTimeout = setTimeout(() => {
            this.setIntervalToUpdateCountdown(props)
        }, (secondsBeforeIsOver % updateIntervalSeconds + 1) * 1000)
    }

    setTimeoutToShowCountdown(props) {
        const { date, countdownDurationMinutes, timezoneOffset } = props
        if (countdownDurationMinutes) {
            const secondsBeforeIsOver = differenceInSeconds(date, nowDate(timezoneOffset))
            if (secondsBeforeIsOver < SECONDS_IN_A_WEEK) {
                this.showCountdownTimeout = setTimeout(() => {
                    this.showCountdown(props)
                }, (secondsBeforeIsOver - countdownDurationMinutes * 60 + 2) * 1000)
            }
        }
    }

    setTimeoutToShowSpinner({ date, spinnerDurationSeconds, timezoneOffset }) {
        if (spinnerDurationSeconds)
        {
            const secondsBeforeIsOver = differenceInSeconds(date, nowDate(timezoneOffset))
            if (secondsBeforeIsOver < SECONDS_IN_A_WEEK) {
                this.showSpinnerTimeout = setTimeout(() => {
                    clearTimeout(this.firstUpdateCountdownTimeout)
                    clearInterval(this.updateCountdownInterval)
                    this.setState({ current: 'SPINNER' })
                }, (secondsBeforeIsOver - spinnerDurationSeconds / 2) * 1000)
            }
        }
    }

    setCountdownOverTimeout({ spinnerDurationSeconds, date, timezoneOffset, onCountdownFinished }) {
        const secondsBeforeIsOver = differenceInSeconds(date, nowDate(timezoneOffset))
        if (secondsBeforeIsOver < SECONDS_IN_A_WEEK) {
            this.countdownOverTimeout = setTimeout(() => {
                this.clearTimers()
                this.setState({ current: 'OVER' })
                if (onCountdownFinished) {
                    onCountdownFinished()
                }
            }, (secondsBeforeIsOver + (spinnerDurationSeconds > 0 && spinnerDurationSeconds / 2)) * 1000)
        }
    }

    // (spinnerDurationSeconds <= 0 || spinnerDurationSeconds === null) => (no spinner shown)
    shouldShowSpinner({ date, timezoneOffset, spinnerDurationSeconds }) {
        return spinnerDurationSeconds && Math.abs(differenceInSeconds(date, nowDate(timezoneOffset)) < spinnerDurationSeconds / 2)
    }

    // countdownDurationMinutes <= 0 => no countdown
    // countdownDurationMinutes === null => infite countdown (always shown on first render)
    shouldShowCountdown({ date, countdownDurationMinutes = null, timezoneOffset }) {
        const diffMinutes = Math.abs(differenceInSeconds(date, nowDate(timezoneOffset)) / 60)
        return countdownDurationMinutes === null || typeof countdownDurationMinutes === 'undefined' || diffMinutes < countdownDurationMinutes
    }

    render({ date, renderSpinner, renderCountdown, renderDateTime, renderFinished }, { current, remainingTime }) {
        if (current === 'SPINNER') {
            return renderSpinner && renderSpinner()
        } else if (current === 'COUNTDOWN') {
            return renderCountdown && renderCountdown(remainingTime)
        } else if (current === 'DATE_TIME') {
            return renderDateTime && renderDateTime(date)
        } else {
            return renderFinished && renderFinished(date)
        }
    }
}

export default Countdown